import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {liteClient as algoliasearch} from 'algoliasearch/lite';
import {
  Configure,
  Highlight,
  InstantSearch,
  useClearRefinements,
  useCurrentRefinements,
  useInfiniteHits,
  useInstantSearch,
  useRefinementList,
  useSearchBox,
  useStats,
} from 'react-instantsearch';
import debounce from 'lodash/debounce';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Alert from '@mui/material/Alert';
import ButtonBase from '@mui/material/ButtonBase';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import TerrainIcon from '@mui/icons-material/Terrain';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import useTranslation from '../../customHooks/translations';
import {useLanguageContext} from '../../contexts/LanguageContext';
import {DEFAULT_THEME_COLOR} from '../../constants/theme';
import {getFreakPlaceTypeBackgroundColor, getFreakPlaceTypeColor} from '../../utils/commonUtils';
import {
  buildPlacesSearchParams,
  countActivePlacesFilters,
  DEFAULT_PLACES_SEARCH_FILTERS,
  distanceKm,
  formatDistance,
  hitCoordinates,
  htmlToPlainText,
  leafletBoundsToMapBounds,
  pluralCategory,
  PLACES_SEARCH_RADIUS_OPTIONS_KM,
} from '../../utils/placesSearch';
import './styles.css';

const searchClient = algoliasearch(process.env.REACT_APP_ALGOLIA_APP_ID, process.env.REACT_APP_ALGOLIA_API_KEY);

const PLACE_TYPE_ATTRIBUTE = 'typeName';

const placeTypeLabel = (translation, name) => translation.freakPlaces?.[name] || name;

const PlacesSearchInput = ({onFocus, onClose, showClose}) => {
  const translation = useTranslation();
  const {query, refine, clear} = useSearchBox();
  const [value, setValue] = useState(query);
  const debouncedRefine = useMemo(() => debounce((next) => refine(next), 300), [refine]);

  useEffect(() => () => debouncedRefine.cancel(), [debouncedRefine]);

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 1.5,
        height: 48,
        borderRadius: 6,
        border: '1.5px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
        transition: 'border-color .15s',
        '&:focus-within': {borderColor: DEFAULT_THEME_COLOR},
      }}
    >
      <SearchIcon sx={{color: 'text.secondary', mr: 1}}/>
      <InputBase
        fullWidth
        value={value}
        placeholder={translation.placesSearch.placeholder}
        inputProps={{'aria-label': translation.placesSearch.placeholder, type: 'search', enterKeyHint: 'search'}}
        onFocus={onFocus}
        onChange={(event) => {
          setValue(event.target.value);
          debouncedRefine(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && onClose) {
            onClose();
          }
        }}
        sx={{fontSize: 16}}
      />
      {value ? (
        <IconButton
          size="small"
          aria-label={translation.placesSearch.clearAll}
          onClick={() => {
            setValue('');
            debouncedRefine.cancel();
            clear();
          }}
        >
          <CloseIcon fontSize="small"/>
        </IconButton>
      ) : null}
      {showClose ? (
        <Button size="small" onClick={onClose} sx={{ml: 0.5, color: 'text.secondary', whiteSpace: 'nowrap'}}>
          {translation.placesSearch.close}
        </Button>
      ) : null}
    </Paper>
  );
};

const chipSx = (selected, bg = DEFAULT_THEME_COLOR, fg = 'white') => ({
  flexShrink: 0,
  height: 36,
  borderRadius: 18,
  fontWeight: 600,
  border: '1px solid',
  borderColor: selected ? bg : 'divider',
  bgcolor: selected ? bg : 'background.paper',
  color: selected ? fg : 'text.primary',
  '& .MuiChip-icon': {color: selected ? fg : DEFAULT_THEME_COLOR},
  '&:hover': {bgcolor: selected ? bg : 'action.hover', filter: selected ? 'brightness(0.95)' : 'none'},
});

const PlacesFilterBar = ({filters, onFiltersChange, onNearMe, isLocating, canUseMapArea}) => {
  const translation = useTranslation();
  const {items, refine} = useRefinementList({
    attribute: PLACE_TYPE_ATTRIBUTE,
    limit: 20,
    sortBy: ['isRefined:desc', 'count:desc', 'name:asc'],
  });

  const toggleScope = async (scope) => {
    const next = filters.scope === scope ? 'anywhere' : scope;
    if (next === 'nearMe' && !(await onNearMe())) {
      return;
    }
    onFiltersChange({scope: next});
  };

  return (
    <>
      <Box
        role="toolbar"
        aria-label={translation.placesSearch.filters}
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          overflowX: 'auto',
          py: 1.25,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {display: 'none'},
        }}
      >
        <Chip
          icon={isLocating ? <CircularProgress size={14}/> : <MyLocationIcon fontSize="small"/>}
          label={translation.placesSearch.nearMe}
          onClick={() => toggleScope('nearMe')}
          aria-pressed={filters.scope === 'nearMe'}
          sx={chipSx(filters.scope === 'nearMe')}
        />
        {canUseMapArea ? (
          <Chip
            icon={<MapOutlinedIcon fontSize="small"/>}
            label={translation.placesSearch.mapArea}
            onClick={() => toggleScope('mapArea')}
            aria-pressed={filters.scope === 'mapArea'}
            sx={chipSx(filters.scope === 'mapArea')}
          />
        ) : null}
        <Chip
          icon={<PhotoCameraOutlinedIcon fontSize="small"/>}
          label={translation.placesSearch.withPhotos}
          onClick={() => onFiltersChange({withPhotos: !filters.withPhotos})}
          aria-pressed={filters.withPhotos}
          sx={chipSx(filters.withPhotos)}
        />
        {items.length ? <Divider orientation="vertical" flexItem sx={{my: 0.5}}/> : null}
        {items.map((item) => {
          const bg = getFreakPlaceTypeBackgroundColor({name: item.value}) || DEFAULT_THEME_COLOR;
          const fg = getFreakPlaceTypeColor({name: item.value}) || 'white';
          return (
            <Chip
              key={item.value}
              onClick={() => refine(item.value)}
              aria-pressed={item.isRefined}
              label={(
                <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>
                  {placeTypeLabel(translation, item.value)}
                  <Box
                    component="span"
                    sx={{
                      px: 0.75,
                      borderRadius: 2,
                      fontSize: 12,
                      fontWeight: 700,
                      bgcolor: item.isRefined ? 'rgba(0,0,0,0.18)' : 'action.selected',
                    }}
                  >
                    {item.count}
                  </Box>
                </Box>
              )}
              sx={chipSx(item.isRefined, bg, fg)}
            />
          );
        })}
      </Box>
      {filters.scope === 'nearMe' ? (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.25}}>
          <Typography variant="body2" sx={{fontWeight: 600, color: 'text.secondary'}}>
            {translation.placesSearch.radius}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filters.radiusKm}
            onChange={(event, value) => value && onFiltersChange({radiusKm: value})}
            sx={{
              '& .MuiToggleButton-root': {px: 1.5, borderRadius: 4, textTransform: 'none', fontWeight: 600},
              '& .Mui-selected': {bgcolor: `${DEFAULT_THEME_COLOR} !important`, color: 'white !important'},
            }}
          >
            {PLACES_SEARCH_RADIUS_OPTIONS_KM.map((km) => (
              <ToggleButton key={km} value={km}>{km} km</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      ) : null}
    </>
  );
};

const PlaceResultCard = ({hit, userLocation, onClick}) => {
  const translation = useTranslation();
  const distance = formatDistance(distanceKm(userLocation, hitCoordinates(hit)));
  const freaksCount = hit?.map?.Freak?.length || 0;

  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        textAlign: 'left',
        gap: 1.5,
        p: 1.25,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        transition: 'box-shadow .15s, transform .15s',
        '&:hover': {boxShadow: '0 6px 18px rgba(0,0,0,0.10)', transform: 'translateY(-1px)'},
      }}
    >
      {hit.imageUrl ? (
        <Box
          component="img"
          src={hit.imageUrl}
          alt=""
          loading="lazy"
          sx={{width: {xs: 76, sm: 96}, height: {xs: 76, sm: 96}, borderRadius: 2, objectFit: 'cover', flexShrink: 0}}
        />
      ) : (
        <Box sx={{
          width: {xs: 76, sm: 96},
          height: {xs: 76, sm: 96},
          borderRadius: 2,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
        }}>
          <TerrainIcon sx={{color: DEFAULT_THEME_COLOR, fontSize: 36}}/>
        </Box>
      )}
      <Box sx={{flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.4}}>
        {hit?.type?.name ? (
          <Box
            component="span"
            sx={{
              alignSelf: 'flex-start',
              px: 1,
              py: 0.2,
              borderRadius: 1.5,
              fontSize: 11,
              fontWeight: 700,
              border: '1px solid rgba(0,0,0,0.12)',
              bgcolor: getFreakPlaceTypeBackgroundColor(hit.type),
              color: getFreakPlaceTypeColor(hit.type) || 'black',
            }}
          >
            {placeTypeLabel(translation, hit.type.name)}
          </Box>
        ) : null}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 15,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '& mark': {bgcolor: 'transparent', color: DEFAULT_THEME_COLOR},
          }}
        >
          {hit._highlightResult?.title ? <Highlight hit={hit} attribute="title"/> : hit.title}
        </Typography>
        {hit.description ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {htmlToPlainText(hit.description)}
          </Typography>
        ) : null}
        {distance || freaksCount ? (
          <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.25}}>
            {distance ? (
              <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, borderRadius: 2, bgcolor: 'action.hover', fontSize: 12, fontWeight: 600}}>
                <PlaceOutlinedIcon sx={{fontSize: 14, color: DEFAULT_THEME_COLOR}}/>{distance}
              </Box>
            ) : null}
            {freaksCount ? (
              <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, borderRadius: 2, bgcolor: 'action.hover', fontSize: 12, fontWeight: 600}}>
                <GroupsOutlinedIcon sx={{fontSize: 14, color: '#FFB400'}}/>
                {translation.formatString(translation.placesSearch.freaksHere, freaksCount)}
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
      <ChevronRightIcon sx={{color: 'text.secondary', display: {xs: 'none', sm: 'block'}}}/>
    </ButtonBase>
  );
};

const PlacesResults = ({filters, userLocation, onSelect, onClearFilters, selectedTypesCount}) => {
  const translation = useTranslation();
  const {language} = useLanguageContext();
  const {hits, isLastPage, showMore} = useInfiniteHits();
  const {nbHits} = useStats();
  const {status} = useInstantSearch();
  const sentinelRef = useRef(null);
  const isLoading = status === 'loading' || status === 'stalled';
  const activeCount = countActivePlacesFilters(filters, selectedTypesCount);

  // auto-load the next page when the end of the list scrolls into view
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || isLastPage || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        showMore();
      }
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isLastPage, showMore, hits.length]);

  const category = pluralCategory(nbHits, language);
  const resultsKey = {zero: 'resultsZero', one: 'resultsOne', few: 'resultsFew', many: 'resultsMany'}[category];

  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1}}>
        <Typography variant="body2" sx={{fontWeight: 600, color: 'text.secondary'}} aria-live="polite">
          {translation.formatString(translation.placesSearch[resultsKey], nbHits)}
        </Typography>
        {activeCount > 0 ? (
          <Button size="small" onClick={onClearFilters} sx={{fontWeight: 700, color: DEFAULT_THEME_COLOR, textTransform: 'none'}}>
            {translation.placesSearch.clearAll} ({activeCount})
          </Button>
        ) : null}
      </Box>
      <Box sx={{height: 3, mb: 1}}>
        {isLoading ? <LinearProgress sx={{height: 3, borderRadius: 2}}/> : null}
      </Box>

      {!hits.length && !isLoading ? (
        <Box sx={{textAlign: 'center', py: 5, px: 2}}>
          <Box sx={{
            width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover',
          }}>
            <TravelExploreIcon sx={{fontSize: 36, color: DEFAULT_THEME_COLOR}}/>
          </Box>
          <Typography sx={{fontWeight: 700}}>{translation.placesSearch.noResultsTitle}</Typography>
          <Typography variant="body2" sx={{color: 'text.secondary', mt: 0.5}}>
            {translation.placesSearch.noResultsHint}
          </Typography>
          {activeCount > 0 ? (
            <Button
              variant="contained"
              disableElevation
              startIcon={<FilterAltOffOutlinedIcon/>}
              onClick={onClearFilters}
              sx={{mt: 2.5, borderRadius: 6, bgcolor: DEFAULT_THEME_COLOR, '&:hover': {bgcolor: DEFAULT_THEME_COLOR}}}
            >
              {translation.placesSearch.clearFilters}
            </Button>
          ) : null}
        </Box>
      ) : (
        <Box component="ul" sx={{listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1.25}}>
          {hits.map((hit) => (
            <Box component="li" key={hit.objectID}>
              <PlaceResultCard hit={hit} userLocation={userLocation} onClick={() => onSelect(hit)}/>
            </Box>
          ))}
        </Box>
      )}

      {!isLastPage && hits.length ? (
        <Box ref={sentinelRef} sx={{display: 'flex', justifyContent: 'center', py: 2}}>
          <Button size="small" onClick={showMore} sx={{textTransform: 'none', color: DEFAULT_THEME_COLOR}}>
            {translation.placesSearch.loadMore}
          </Button>
        </Box>
      ) : null}

      <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, py: 1, opacity: 0.7}}>
        <Box component="img" src="./algolia-logo.png" alt="" sx={{width: 14, height: 14, borderRadius: 0.5}}/>
        <Typography sx={{fontSize: 11, fontWeight: 600}}>Algolia</Typography>
      </Box>
    </Box>
  );
};

const PlacesSearch = ({context, handleCardClick, loadOnStart, mapRef, overlay, compact}) => {
  const translation = useTranslation();
  const [filters, setFilters] = useState(DEFAULT_PLACES_SEARCH_FILTERS);
  const [userLocation, setUserLocation] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationDenied, setLocationDenied] = useState(false);
  const [mapBounds, setMapBounds] = useState(null);
  const [isOpen, setIsOpen] = useState(!!loadOnStart);
  const {query} = useSearchBox();
  const {items: currentRefinements} = useCurrentRefinements({includedAttributes: [PLACE_TYPE_ATTRIBUTE]});
  const {refine: clearTypes} = useClearRefinements({includedAttributes: [PLACE_TYPE_ATTRIBUTE]});
  const selectedTypesCount = currentRefinements.reduce((acc, item) => acc + item.refinements.length, 0);
  const canUseMapArea = !!mapRef?.current;

  const updateFilters = useCallback((next) => {
    setFilters((previous) => ({...previous, ...next}));
    setIsOpen(true);
  }, []);

  // open the results as soon as there is something to show
  useEffect(() => {
    if (query || selectedTypesCount) {
      setIsOpen(true);
    }
  }, [query, selectedTypesCount]);

  // "This map area" follows the map while it is active
  useEffect(() => {
    const map = mapRef?.current;
    if (filters.scope !== 'mapArea' || !map) {
      return undefined;
    }
    const sync = () => setMapBounds(leafletBoundsToMapBounds(map.getBounds()));
    sync();
    map.on('moveend', sync);
    return () => map.off('moveend', sync);
  }, [filters.scope, mapRef]);

  const requestNearMe = useCallback(() => new Promise((resolve) => {
    if (userLocation) {
      resolve(true);
      return;
    }
    if (!navigator.geolocation) {
      setLocationDenied(true);
      resolve(false);
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({latitude: position.coords.latitude, longitude: position.coords.longitude});
        setLocationDenied(false);
        setIsLocating(false);
        resolve(true);
      },
      () => {
        setLocationDenied(true);
        setIsLocating(false);
        resolve(false);
      },
      {enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000},
    );
  }), [userLocation]);

  const searchParams = useMemo(
    () => buildPlacesSearchParams({context, filters, userLocation, mapBounds}),
    [context, filters, userLocation, mapBounds],
  );

  const clearFilters = useCallback(() => {
    clearTypes();
    setFilters(DEFAULT_PLACES_SEARCH_FILTERS);
    setLocationDenied(false);
  }, [clearTypes]);

  const showResults = !overlay || isOpen;

  const results = (
    <PlacesResults
      filters={filters}
      userLocation={userLocation}
      selectedTypesCount={selectedTypesCount}
      onClearFilters={clearFilters}
      onSelect={(hit) => {
        handleCardClick(hit);
        if (overlay) {
          setIsOpen(false);
        }
      }}
    />
  );

  const filterControls = (
    <>
      <PlacesFilterBar
        filters={filters}
        onFiltersChange={updateFilters}
        onNearMe={requestNearMe}
        isLocating={isLocating}
        canUseMapArea={canUseMapArea}
      />
      {locationDenied ? (
        <Alert severity="warning" onClose={() => setLocationDenied(false)} sx={{mb: 1, borderRadius: 2}}>
          {translation.placesSearch.locationDenied}
        </Alert>
      ) : null}
    </>
  );

  return (
    <Box sx={{position: 'relative', width: '100%', maxWidth: overlay ? 760 : 'none', mx: 'auto', px: overlay && !compact ? 2 : 0, pt: overlay && !compact ? 1 : 0}}>
      <Configure {...searchParams} hitsPerPage={20}/>
      <PlacesSearchInput
        onFocus={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        showClose={overlay && isOpen}
      />
      {compact ? null : filterControls}
      {showResults ? (
        overlay ? (
          <Paper
            elevation={8}
            sx={{
              position: 'absolute',
              top: '100%',
              left: compact ? 0 : 16,
              right: compact ? 0 : 16,
              mt: compact ? 1 : 0,
              zIndex: 1000,
              maxHeight: '70vh',
              overflowY: 'auto',
              borderRadius: 3,
              px: 2,
              pb: 1,
            }}
          >
            {compact ? <Box sx={{pt: 0.5}}>{filterControls}</Box> : null}
            {results}
          </Paper>
        ) : results
      ) : null}
    </Box>
  );
};

/**
 * Algolia places search with filters (near me + radius, this map area, with
 * photos, place types with counts).
 *
 * - `overlay` (desktop): results float over the map below the search bar.
 * - `compact` (with `overlay`): filters move into the floating panel, for toolbars.
 * - otherwise (mobile drawer): results are rendered inline.
 */
const PlacesSearchComponent = ({indexName, context, handleCardClick, loadOnStart, mapRef, overlay, compact}) => (
  <InstantSearch key={context} indexName={indexName} searchClient={searchClient}>
    <PlacesSearch
      context={context}
      handleCardClick={handleCardClick}
      loadOnStart={loadOnStart}
      mapRef={mapRef}
      overlay={overlay}
      compact={compact}
    />
  </InstantSearch>
);

export default PlacesSearchComponent;
